import { Injectable } from '@angular/core';
import * as moment from 'moment';

import { Event } from '../../models/event';

import { BackendBaseService } from '../backend-base.service';
import { HttpClientService } from '../common/http-client.service';

@Injectable()
export class EventService extends BackendBaseService {
  timezoneOffset: number;

  timezone: string;

  url: string = 'events';

  private formOptionsPromise: Promise<any>;

  constructor(public httpClient: HttpClientService) {
    super(httpClient);
  }

  // HTTP calls

  fetchMatterInfo(matterId: number): Promise<any> {
    return this.httpClient.get(`${this.url}/fetch_matter_info.json`, { angular: true, matter_id: matterId });
  }

  /**
   * Get form options for the form
   * @return {Promise<any>} Server response with form options
   */
  formOptions(): Promise<any> {
    if (!this.formOptionsPromise)
      this.formOptionsPromise = this.httpClient.get(`${this.url}.json`, { form_options: true }).then((response_json) => {
        if (response_json.timezone_offset) this.timezoneOffset = response_json.timezone_offset;
        return response_json;
      });
    return this.formOptionsPromise;
  }

  getEvents(data: any): Promise<any> {
    return this.httpClient.post(`${this.url}/get_events.json`, data);
  }

  /**
   * Events for the home page
   * @return {Promise<any>} Server response with events
   */
  home(): Promise<any> {
    return this.httpClient.get(`${this.url}.json`, { home: true });
  }

  // Event processing/formatting methods

  /**
   * Add offset to moment date
   * @param  {any}       m_obj Moment date object
   * @param  {number =     null}        offset Offset to add
   * @return {any}             Returns Moment date object
   */
  addOffset(m_obj: any, offset: number | null = null): any {
    if (offset) {
      m_obj = moment(m_obj);
      console.log(m_obj);
      m_obj.utcOffset(this.timezoneOffset);
      m_obj.add(offset / 60 - this.timezoneOffset, 'minutes');
      console.log(m_obj);
      return m_obj;
    } else if (this.timezoneOffset) {
      return m_obj.utcOffset(this.timezoneOffset);
    } else return m_obj;
  }

  bydayForMonth(byday: string): string {
    if (byday.length > 2) {
      let week_num: string = byday.substr(0, 1);
      week_num = this.formatNumber(week_num) + ' ' + byday.substr(1);
      return week_num;
    } else return byday;
  }

  checkIfAllDay(start: any, end: any): boolean {
    let duration_hours: number = moment.duration(end - start).hours();
    if (duration_hours > 0 && (duration_hours % 14 == 0 || (duration_hours > 14 && duration_hours < 24))) return true;
    else return false;
  }

  formatEventReminder(duration: any): Array<any> {
    let reminder: number = parseInt(duration) / 60;
    let reminder_time: string = reminder > 1 ? ' minutes' : ' minute';
    if (reminder > 59) {
      reminder = reminder / 60;
      reminder_time = reminder > 1 ? ' hours' : ' hour';
      if (reminder > 23) {
        reminder = reminder / 24;
        reminder_time = reminder > 1 ? ' days' : ' day';
      }
    }
    return [reminder, reminder_time];
  }

  formatEventRrule(rrule: string): string {
    if (rrule) {
      let rules: Array<string> = rrule.split(';');
      let rule_obj: any = {};
      let repeat: string = '';
      for (let x in rules) rule_obj[rules[x].split('=')[0]] = rules[x].split('=')[1];
      repeat += 'Every ';
      rule_obj['INTERVAL'] = parseInt(rule_obj['INTERVAL']);
      let until: string = ' - till forever';
      if (rule_obj['UNTIL']) until = ' - till ' + rule_obj['UNTIL'].substr(4, 2) + '-' + rule_obj['UNTIL'].substr(6, 2) + '-' + rule_obj['UNTIL'].substr(0, 4);
      switch (rule_obj['FREQ']) {
        case 'DAILY':
          repeat += rule_obj['INTERVAL'] > 1 ? rule_obj['INTERVAL'] + ' days' : ' day';
          break;
        case 'WEEKLY':
          repeat += rule_obj['INTERVAL'] > 1 ? rule_obj['INTERVAL'] + ' weeks' : ' week';
          if (rule_obj['BYDAY']) repeat += ' on ' + rule_obj['BYDAY'] + ' days';
          break;
        case 'MONTHLY':
          repeat += rule_obj['INTERVAL'] > 1 ? rule_obj['INTERVAL'] + ' months' : ' month';
          if (rule_obj['BYDAY']) {
            repeat += ' on ' + this.bydayForMonth(rule_obj['BYDAY']) + ' day of month';
          } else if (rule_obj['BYMONTHDAY']) {
            repeat += ' on ' + this.formatNumber(rule_obj['BYMONTHDAY']) + ' day of month';
          }
          break;
        case 'YEARLY':
          repeat += rule_obj['INTERVAL'] > 1 ? rule_obj['INTERVAL'] + ' years' : ' year';
          break;
      }
      return repeat + until;
    } else return 'Does not repeat';
  }

  formatEventTitleFullDate(start: any, end: any, short: boolean, all_day: boolean, date_required: boolean = false): string {
    if (all_day) return 'All day';
    let stime: string = '';
    let etime: string = '';
    if (start.format('MM-DD-YYYY') != end.format('MM-DD-YYYY') && date_required) {
      if (short) {
        stime = start.format('MM-DD-YYYY');
        etime = end.format('MM-DD-YYYY');
      } else {
        stime = start.format('MM-DD-YYYY');
        etime = end.format('MM-DD-YYYY');
      }
    } else if (date_required) stime = start.format('MM-DD-YYYY');
    let sa: string = start.format('a').substring(0, 1);
    let ea: string = end.format('a').substring(0, 1);
    let sm: string = start.format(':mm');
    if (sm == ':00') sm = '';
    let em: string = end.format(':mm');
    if (em == ':00') em = '';
    sa = start.format('h') + sm + sa;
    ea = end.format('h') + em + ea;
    if (stime != '') sa = stime + ' ' + sa + ' - ' + etime + ' ' + ea;
    else if (sa != ea) sa += '-' + ea;
    return sa;
  }

  formatNumber(num: string): string {
    if (num == '1') num += 'st';
    else if (num == '2') num += 'nd';
    else if (num == '3') num += 'rd';
    else num += 'th';
    return num;
  }

  getEventTitle(start: string, end: string, s_offset: number, e_offset: number): string {
    return this.formatEventTitleFullDate(moment(start), moment(end), false, this.checkIfAllDay(moment(start), moment(end)), false);
  }

  /**
   * Moment object from struct
   * @param  {any} struct [description]
   * @return {any}        [description]
   */
  moment(struct: any): any {
    return this.addOffset(moment()).set({
      year: struct.year,
      month: struct.month - 1,
      date: struct.day,
      hour: struct.hour,
      minute: struct.minute,
      second: struct.second,
    });
  }

  /**
   * Fetch overlapping events for given dates and attendees
   * @param  {any       = {}}        params Request params
   * @return {Promise<any>}   Response as JSON
   */
  overlappingEvents(params: any = {}): Promise<any> {
    params.overlapping = true;
    return this.httpClient.get(`${this.url}.json`, params);
  }

  /**
   * Save event
   * @param  {Event}        event Event to save
   * @return {Promise<any>}       Server response as JSON
   */
  save(event: Event): Promise<any> {
    return super.save({
      id: event.id,
      event: event,
      reminder_delivery_methods: event.reminder_delivery_methods
    });
  }

  /**
   * Set offset for moment date
   * @param  {any} momentDate Moment date object to set offset for
   * @return {any}            Moment date object with offset set
   */
  setOffset(momentDate: any, offset: any = null): any {
    return this.addOffset(moment(), offset).set({
      year: momentDate.year(),
      month: momentDate.month() + 1,
      day: momentDate.date(),
      hour: momentDate.hour(),
      minute: momentDate.minute(),
      second: momentDate.second(),
    });
  }

  /**
   * String to moment date
   * @param  {string}    dateString Date string to be parsed into moment date
   * @param  {string =          null}        momentFormat Moment date format to be used for parsing string (optional, will use default parsing otherwise)
   * @return {any}                  Moment date with offset correctly
   */
  strToMoment(dateString: string, momentFormat: string | null = null, offset: any = null): any {
    try {
      if (momentFormat) return this.setOffset(moment(dateString, momentFormat), offset);
      else return this.setOffset(moment(dateString), offset);
    } catch (err) {
      console.error(err, dateString, momentFormat, offset);
    }
  }
}
